import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../utils/supabase';
import { Camera, Save, Store, Phone, MapPin, FileText, Palette, Globe, CheckCircle, Clock } from 'lucide-react';

const themes = [
  { name: 'Green', primary: '#16a34a', bg: 'bg-green-500' },
  { name: 'Blue', primary: '#2563eb', bg: 'bg-blue-600' },
  { name: 'Red', primary: '#dc2626', bg: 'bg-red-600' },
  { name: 'Purple', primary: '#9333ea', bg: 'bg-purple-600' },
  { name: 'Orange', primary: '#ea580c', bg: 'bg-orange-600' },
];

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [restaurantId, setRestaurantId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    name_ar: '',
    phone: '',
    address: '',
    description: '',
    theme_color: '#16a34a',
    website: '',
    opening_hours: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setUserEmail(user.email);

      const { data: rest } = await supabase
        .from('restaurants')
        .select('*')
        .eq('email', user.email)
        .single();

      if (!rest) return;
      setRestaurantId(rest.id);
      setLogoUrl(rest.logo_url || null);
      setForm({
        name: rest.name || '',
        name_ar: rest.name_ar || '',
        phone: rest.phone || '',
        address: rest.address || '',
        description: rest.description || '',
        theme_color: rest.theme_color || '#16a34a',
        website: rest.website || '',
        opening_hours: rest.opening_hours || '',
      });

      document.documentElement.style.setProperty('--color-primary', rest.theme_color || '#16a34a');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !restaurantId) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${restaurantId}/logo.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('restaurant-logos')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('restaurant-logos')
        .getPublicUrl(fileName);

      setLogoUrl(publicUrl);

      await supabase
        .from('restaurants')
        .update({ logo_url: publicUrl })
        .eq('id', restaurantId);

    } catch (err) {
      console.error(err);
      alert('Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!restaurantId) return;
    setSaving(true);
    setSuccess(false);
    try {
      const { error } = await supabase
        .from('restaurants')
        .update({
          name: form.name.trim(),
          name_ar: form.name_ar.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          description: form.description.trim(),
          theme_color: form.theme_color,
          website: form.website.trim(),
          opening_hours: form.opening_hours.trim(),
        })
        .eq('id', restaurantId);

      if (error) throw error;

      document.documentElement.style.setProperty('--color-primary', form.theme_color);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error(err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-3xl">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse h-48" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Restaurant Settings</h2>
          <p className="text-sm text-gray-400 mt-0.5">{userEmail}</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-secondary transition disabled:opacity-60 shadow-sm"
        >
          {saving ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Saving...
            </>
          ) : (
            <>
              <Save size={16} />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Success Banner */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2">
          <CheckCircle size={16} className="text-green-500" />
          Changes saved successfully!
        </div>
      )}

      {/* Logo Section */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Camera size={18} className="text-gray-400" />
          <h3 className="font-bold text-gray-800">Restaurant Logo</h3>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-6">
            <div className="relative shrink-0">
              <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden bg-gray-50">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: form.theme_color }}
                  >
                    <span className="text-white font-bold text-2xl">
                      {form.name?.[0]?.toUpperCase() || 'T'}
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center shadow-md hover:bg-secondary transition disabled:opacity-60"
              >
                {uploadingLogo ? (
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : (
                  <Camera size={14} />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
            </div>
            <div>
              <p className="font-bold text-gray-800 text-lg">{form.name || 'My Restaurant'}</p>
              {form.name_ar && <p className="text-gray-500 text-sm mt-0.5" dir="rtl">{form.name_ar}</p>}
              <p className="text-sm text-gray-400 mt-2">
                {uploadingLogo ? '⏳ Uploading...' : 'Click the camera icon to upload your logo'}
              </p>
              <p className="text-xs text-gray-300 mt-1">PNG or JPG, max 2MB</p>
            </div>
          </div>
        </div>
      </div>

      {/* Basic Info */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Store size={18} className="text-gray-400" />
          <h3 className="font-bold text-gray-800">Basic Information</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Name (English) *</label>
              <input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="My Restaurant"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">الاسم بالعربي</label>
              <input
                value={form.name_ar}
                onChange={e => setForm({ ...form, name_ar: e.target.value })}
                placeholder="مطعمي"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary text-right transition"
                dir="rtl"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-1">
              <FileText size={14} className="text-gray-400" /> Description
            </label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Tell customers what makes your restaurant special..."
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none transition"
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Contact & Location */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Phone size={18} className="text-gray-400" />
          <h3 className="font-bold text-gray-800">Contact & Location</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">WhatsApp Number</label>
              <input
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="+966 5xx xxx xxxx"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-1">
                <Globe size={14} className="text-gray-400" /> Website
              </label>
              <input
                value={form.website}
                onChange={e => setForm({ ...form, website: e.target.value })}
                placeholder="https://myrestaurant.com"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-1">
              <MapPin size={14} className="text-gray-400" /> Address
            </label>
            <input
              value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })}
              placeholder="Al Rawdah, Jeddah"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block flex items-center gap-1">
              <Clock size={14} className="text-gray-400" /> Opening Hours
            </label>
            <input
              value={form.opening_hours}
              onChange={e => setForm({ ...form, opening_hours: e.target.value })}
              placeholder="Sat-Thu: 11am - 12am, Fri: 1pm - 12am"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition"
            />
          </div>
        </div>
      </div>

      {/* Theme */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Palette size={18} className="text-gray-400" />
          <h3 className="font-bold text-gray-800">Color Theme</h3>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-400 mb-4">Choose a color that represents your brand</p>
          <div className="flex gap-4 flex-wrap">
            {themes.map(theme => (
              <button
                key={theme.name}
                onClick={() => {
                  setForm({ ...form, theme_color: theme.primary });
                  document.documentElement.style.setProperty('--color-primary', theme.primary);
                }}
                className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition
                  ${form.theme_color === theme.primary ? 'border-gray-800 scale-105 shadow-md' : 'border-transparent hover:border-gray-200'}`}
              >
                <div
                  className="w-12 h-12 rounded-xl shadow-md flex items-center justify-center"
                  style={{ backgroundColor: theme.primary }}
                >
                  {form.theme_color === theme.primary && (
                    <CheckCircle size={20} className="text-white" />
                  )}
                </div>
                <span className="text-xs font-semibold text-gray-600">{theme.name}</span>
              </button>
            ))}
          </div>

          {/* Live Preview */}
          <div className="mt-6 bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-400 font-medium mb-3 uppercase tracking-wide">Preview</p>
            <div className="bg-white rounded-xl p-4 shadow-sm flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: form.theme_color }}
                >
                  {form.name?.[0]?.toUpperCase() || 'T'}
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">{form.name || 'My Restaurant'}</p>
                  <p className="text-xs text-gray-400">{form.address || 'Jeddah'}</p>
                </div>
              </div>
              <button
                className="px-4 py-2 rounded-xl text-white text-xs font-medium"
                style={{ backgroundColor: form.theme_color }}
              >
                View Orders
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Save Bottom */}
      <div className="flex justify-end pb-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-xl font-medium hover:bg-secondary transition disabled:opacity-60 shadow-sm"
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

    </div>
  );
}